import {useQuery} from '@tanstack/react-query'
import {Server, Box, KeyRound, Tag} from 'lucide-react'
import {useParams} from 'react-router-dom'
import {InlineAttributeEditor} from '@/components/attributes/InlineAttributeEditor'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {CardSection} from '@/components/shared/CardSection'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {RiskLevelBadge} from '@/components/ui/risk-level-badge'
import {PageLayout} from '@/components/shared/PageLayout'
import {IconText} from '@/components/shared/IconText'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {AccessProfileStatus} from '@/types/access-profile'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatTimestamp} from '@/lib/date-utils'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {EntityCardLink} from '@/components/shared/EntityCardLink'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {DetailField} from '@/components/shared/DetailField'
import {DetailList} from '@/components/shared/DetailList'
import {CONCEPT_ICON} from '@/lib/concepts'

export function EnforcementObjectDetail() {
    const services = useServices()
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    usePageTitle(i18n.enforcementObjects.detailPageTitle)
    const {id} = useParams<{ id: string }>()
    const canEditAttributes = hasPermission(permissions.attributeDefinitions.eoUpdate)

    // Fetch enforcement object
    const {data: enforcementObject, isLoading, error} = useQuery({
        queryKey: ['enforcement-object', id],
        queryFn: () => services.enforcementObjects.detail(id!),
        enabled: !!id,
    })

    // Fetch linked access profiles
    const {data: linkedProfiles} = useQuery({
        queryKey: ['access-profiles-by-eo', id],
        queryFn: () => services.accessProfiles.byEnforcementObject(id!),
        enabled: !!id,
    })

    const {enforcementTypes} = useAppConfig()

    const eo = enforcementObject

    return (
        <PageLayout
            data-testid="enforcement-object-detail-page"
            variant="detail"
            title={enforcementObject?.derivedDisplayName ?? ''}
            icon={CONCEPT_ICON.enforcementObject}
            backTo={routes.systems.enforcementObjects.list()}
            loading={isLoading}
            error={error || (!isLoading && !enforcementObject ? new Error('Enforcement object not found') : undefined)}
            actions={enforcementObject ? (
                <Badge variant="outline">
                    {labelOf(enforcementTypes, enforcementObject.enforcementType)}
                </Badge>
            ) : undefined}
        >
            {eo && (<>
                <DetailsCard
                    data-testid="card-enforcement-object-details"
                    title={i18n.enforcementObjects.objectDetails}
                    description={i18n.enforcementObjects.technicalConfig}
                    fields={[
                        {
                            label: i18n.enforcementObjects.externalIdentifier,
                            value: <CodeBlock>{eo.externalIdentifier}</CodeBlock>,
                            fullWidth: true,
                        },
                        {label: i18n.enforcementObjects.enforcementType, value: labelOf(enforcementTypes, eo.enforcementType)},
                        {label: i18n.common.createdAt, value: formatTimestamp(eo.createdAt)},
                        {label: i18n.common.lastUpdated, value: formatTimestamp(eo.updatedAt)},
                    ]}
                />

                <CardSection data-testid="section-attributes" icon={Tag} title={i18n.common.attributes}>
                    <InlineAttributeEditor
                        entityType={AttributeEntityType.ENFORCEMENT_OBJECT}
                        entityId={id!}
                        entityTypeCode={eo.enforcementType}
                        canEdit={canEditAttributes}
                    />
                </CardSection>

                {/* Related Entities */}
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-related-entities">{i18n.enforcementObjects.relatedEntities}</CardTitle>
                        <CardDescription>{i18n.enforcementObjects.systemAndResourceAssociations}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <DetailList columns={2} className="gap-6">
                            <DetailField
                                label={<IconText icon={Server}>{i18n.systems.title}</IconText>}
                                value={
                                    <EntityCardLink
                                        data-testid="link-system-detail"
                                        to={routes.systems.detail(eo.targetSystem.id)}
                                        icon={Server}
                                        density="compact"
                                    >
                                        {eo.targetSystem.displayName}
                                    </EntityCardLink>
                                }
                            />
                            <DetailField
                                label={<IconText icon={Box}>{i18n.common.resource}</IconText>}
                                value={eo.resource ? (
                                    <EntityCardLink
                                        data-testid="link-resource-detail"
                                        to={routes.systems.resources.detail(eo.resource.id)}
                                        icon={Box}
                                        density="compact"
                                    >
                                        {eo.resource.displayName}
                                    </EntityCardLink>
                                ) : (
                                    <EntityCardLink
                                        icon={Box}
                                        density="compact"
                                    >
                                        {i18n.enforcementObjects.notLinkedToResource}
                                    </EntityCardLink>
                                )}
                            />
                        </DetailList>
                    </CardContent>
                </Card>

                {/* Access Profiles */}
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-access-profiles">
                            <IconText icon={KeyRound}>{i18n.enforcementObjects.accessProfiles}</IconText>
                        </CardTitle>
                        <CardDescription>{i18n.enforcementObjects.accessProfilesDesc}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {linkedProfiles && linkedProfiles.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                                        <TableHead data-testid="col-risk-level">{i18n.accessProfiles.riskLevel}</TableHead>
                                        <TableHead data-testid="col-resource">{i18n.common.resource}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {linkedProfiles.map(profile => (
                                        <TableRow key={profile.id}>
                                            <TableCell>
                                                <TextLink
                                                    to={routes.accessGovernance.profiles.detail(profile.id)}
                                                    variant="bold"
                                                    testId={`link-profile-${profile.id}`}
                                                >
                                                    {profile.name}
                                                </TextLink>
                                                {profile.description && (
                                                    <MutedText size="xs" block className="mt-0.5">{profile.description}</MutedText>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={profile.status === AccessProfileStatus.ACTIVE ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                                                    {profile.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <RiskLevelBadge riskLevel={profile.riskLevel}/>
                                            </TableCell>
                                            <TableCell>
                                                <EntityLink value={profile.resource} testId={`link-resource-${profile.resource.id}`}/>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <MutedText block centered className="py-4">{i18n.enforcementObjects.noAccessProfiles}</MutedText>
                        )}
                    </CardContent>
                </Card>
            </>)}
        </PageLayout>
    )
}
